import React from "react";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import {
  CloseRounded,
  DownloadRounded,
  OpenInNewRounded,
} from "@mui/icons-material";
import color from "../../../Constants/colors";
import { useDocumentCraftingFileObjectUrl } from "../../../Query/useDocumentCrafting";

interface Props {
  open: boolean;
  onClose: () => void;
  /** API path of the PDF (`documentCraftingFileUrls`). */
  url: string;
  title: string;
  /** Shown above the PDF, e.g. the machine-translation notice. */
  notice?: React.ReactNode;
  onDownload?: () => void;
  downloading?: boolean;
}

/**
 * A PDF of the module in an iframe, loaded with the bearer token and the
 * admin workspace header (PdfPreviewModal sends neither header); "Open in
 * new tab" opens the same object URL.
 */
const PdfViewerDialog: React.FC<Props> = ({
  open,
  onClose,
  url,
  title,
  notice,
  onDownload,
  downloading,
}) => {
  const { t } = useTranslation("documentCrafting");
  const pdf = useDocumentCraftingFileObjectUrl(url, open);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle
        sx={{ display: "flex", alignItems: "center", gap: 1, pr: 1, flexWrap: "wrap" }}
      >
        <Box component="span" sx={{ flex: 1, minWidth: 0, overflowWrap: "anywhere" }}>
          {title}
        </Box>
        <Button
          size="small"
          color="secondary"
          startIcon={<OpenInNewRounded />}
          component="a"
          href={pdf.src ?? undefined}
          target="_blank"
          rel="noopener noreferrer"
          disabled={!pdf.src}
        >
          {t("detail.preview.openInNewTab")}
        </Button>
        {onDownload && (
          <Button
            size="small"
            color="secondary"
            startIcon={
              downloading ? <CircularProgress size={14} /> : <DownloadRounded />
            }
            onClick={onDownload}
            disabled={downloading}
          >
            {t("common.download")}
          </Button>
        )}
        <IconButton onClick={onClose} aria-label={t("common.close")}>
          <CloseRounded />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {notice}
        {pdf.src ? (
          <Box
            component="iframe"
            src={pdf.src}
            title={title}
            sx={{
              display: "block",
              width: "100%",
              height: "75vh",
              border: `1px solid ${color.gray200}`,
              borderRadius: "8px",
            }}
          />
        ) : pdf.isError ? (
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={() => pdf.refetch()}>
                {t("common.retry")}
              </Button>
            }
          >
            {t("detail.preview.pdfError")}
          </Alert>
        ) : (
          <Box
            sx={{
              height: "50vh",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexDirection: "column",
              gap: 1.5,
            }}
          >
            <CircularProgress size={28} />
            <Typography sx={{ fontSize: 13, color: color.gray500 }}>
              {t("detail.preview.pdfLoading")}
            </Typography>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PdfViewerDialog;
